import type { Concept } from '@yuan-math/curriculum';

/**
 * Calculus II — the first-year university course.
 *
 * The Grade 12 pathway in `calculus.ts` ends at the fundamental theorem, which
 * is the right place for a school course to stop and a strange place for a
 * student to stop: it hands over a machine for evaluating integrals and then
 * only ever feeds it polynomials. Every integral in that course is one a
 * student can antidifferentiate by reading the power rule backwards. Almost no
 * integral in the world is.
 *
 * These twenty concepts are what happens when the restriction is lifted, and
 * they fall into three genuinely different kinds of work, which is why the
 * course feels like three courses to the people sitting it:
 *
 *  1. **Techniques** — undoing each differentiation rule in turn. Every method
 *     in the first unit is one rule of Grade 12 run backwards, and saying which
 *     is the fastest way to make the unit look like six ideas instead of six
 *     tricks.
 *  2. **Applications** — the integral as a sum of infinitely many small
 *     contributions rather than as an area. Arc length, surface area, work and
 *     centre of mass are one habit used four times: write down the contribution
 *     of a slice, then integrate it.
 *  3. **Series** — the return of the limit, now taken along a sequence. This is
 *     the part with no Grade 12 counterpart at all, and the part that first-year
 *     students fail.
 *
 * The orderings that are decisions rather than conventions:
 *
 * `u-substitution` is here rather than in the Grade 12 pathway, and it is the
 * one concept on this list that is not, in most countries, a Calculus II topic.
 * It is here because this platform's Grade 12 course does not teach it: that
 * course derives the fundamental theorem and stops, and a student arriving at
 * integration by parts without substitution cannot do the second half of any
 * worked example in the unit. Everything downstream of `fundamental-theorem`
 * therefore passes through this concept.
 *
 * `integration-by-parts` depends on `derivative-rules` as well as on
 * substitution, because parts *is* the product rule and is taught as its
 * rearrangement rather than as a formula to memorise. A student who meets
 * ∫u dv = uv − ∫v du as a given has no way to reconstruct it, and no way to
 * see why the method sometimes makes the integral worse.
 *
 * `trigonometric-substitution` depends on `trigonometric-integrals` rather than
 * the other way round, which surprises people. Substituting x = a sin θ is easy;
 * the integral it produces is a trigonometric one, and a student who cannot do
 * ∫cos²θ dθ has substituted their way into a wall.
 *
 * `improper-integrals` closes the techniques unit rather than opening the series
 * one, even though the integral test is its main customer. An improper integral
 * is a limit of ordinary ones, and it is the last thing that can be said about
 * evaluating an integral — it belongs with the evaluation, not with the sums.
 *
 * `sequence-limits` is separated from `series-convergence` on purpose, and the
 * separation is the single most valuable thing in the series unit. A sequence
 * and the series built from it are different objects with different limits, and
 * conflating them is what produces the belief that a series converges whenever
 * its terms shrink. Splitting them lets the divergence test be stated as what it
 * is — a one-way implication — rather than as a rule with an unexplained
 * exception called the harmonic series.
 *
 * The convergence tests are ordered by what each can decide rather than by
 * tradition. `integral-test` is first because it *proves* the p-series result
 * every later test compares against; `comparison-tests` needs that stock of
 * known series to compare with; `ratio-test` is last of the positive-term tests
 * because it is the one that ignores the comparison entirely and looks only at
 * the ratio, and it is the one power series need.
 *
 * `power-series` carries the radius of convergence rather than leaving it a
 * concept of its own. A power series without its interval is not a function —
 * it is a formula with no domain — and the two are always found in the same
 * calculation, by the ratio test, in the same breath.
 *
 * `polar-calculus` depends on `parametric-curves` because a polar curve is a
 * parametric curve with θ as the parameter, and every polar formula in the
 * lesson is derived from the parametric one rather than asserted. It also
 * depends on the Grade 12 `polar-coordinates`, which supplies the coordinate
 * system itself; nothing here re-teaches the conversion formulas.
 */
export const CALCULUS_TWO_CONCEPTS: Concept[] = [
  /* ------------------------------------------- techniques of integration */
  {
    id: 'u-substitution',
    title: 'Substitution',
    summary: 'The chain rule run backwards — spotting the inside function that was differentiated and putting it back.',
    strand: 'calculus',
    levels: ['university'],
    prerequisites: ['fundamental-theorem'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'integration-by-parts',
    title: 'Integration by Parts',
    summary: 'The product rule rearranged, which turns one integral into a different one — and the skill is choosing which.',
    strand: 'calculus',
    levels: ['university'],
    prerequisites: ['u-substitution', 'derivative-rules'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'trigonometric-integrals',
    title: 'Trigonometric Integrals',
    summary: 'Powers of sine, cosine, secant and tangent, and the two identities that make every one of them a substitution.',
    strand: 'calculus',
    levels: ['university'],
    prerequisites: ['u-substitution', 'half-angle-formulas'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'trigonometric-substitution',
    title: 'Trigonometric Substitution',
    summary: 'Making a square root disappear by deciding, in advance, that x is a sine or a tangent.',
    strand: 'calculus',
    levels: ['university'],
    prerequisites: ['trigonometric-integrals', 'trig-identities'],
    status: 'available',
    estimatedMinutes: 50,
  },
  {
    id: 'partial-fractions',
    title: 'Partial Fractions',
    summary: 'Splitting one rational function into the simple pieces it was added from, each of which integrates to a logarithm.',
    strand: 'calculus',
    levels: ['university'],
    prerequisites: ['u-substitution', 'rational-functions', 'polynomial-division'],
    status: 'available',
    estimatedMinutes: 50,
  },
  {
    id: 'improper-integrals',
    title: 'Improper Integrals',
    summary: 'Integrating to infinity, or across a point where the function blows up — and the limit that says whether the answer exists.',
    strand: 'calculus',
    levels: ['university'],
    prerequisites: ['u-substitution', 'continuity'],
    status: 'available',
    estimatedMinutes: 45,
  },

  /* ------------------------------------- what an integral adds up, part II */
  {
    id: 'arc-length',
    title: 'Arc Length',
    summary: 'How long a curve is, by adding up hypotenuses of triangles too small to see.',
    strand: 'calculus',
    levels: ['university'],
    prerequisites: ['u-substitution'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'surface-of-revolution',
    title: 'Surface Area of a Solid of Revolution',
    summary: 'Spinning a curve about an axis, and the band of surface each piece of arc sweeps out.',
    strand: 'calculus',
    levels: ['university'],
    prerequisites: ['arc-length'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'work-integrals',
    title: 'Work',
    summary: 'Force times distance, when the force keeps changing — springs, cables and tanks that have to be pumped out.',
    strand: 'calculus',
    levels: ['university'],
    prerequisites: ['u-substitution'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'center-of-mass',
    title: 'Centre of Mass',
    summary: 'The one point a shape balances on, found as an average weighted by how much material sits where.',
    strand: 'calculus',
    levels: ['university'],
    prerequisites: ['work-integrals'],
    status: 'available',
    estimatedMinutes: 45,
  },

  /* --------------------------------------------------- sequences and series */
  {
    id: 'sequence-limits',
    title: 'Sequences and Their Limits',
    summary: 'An infinite list of numbers, and whether it settles anywhere — the limit taken along the integers.',
    strand: 'calculus',
    levels: ['university'],
    prerequisites: ['sequences', 'continuity'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'series-convergence',
    title: 'Series and the Test for Divergence',
    summary: 'Adding a sequence up forever, what that even means, and the one thing shrinking terms do not guarantee.',
    strand: 'calculus',
    levels: ['university'],
    prerequisites: ['sequence-limits', 'series'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'integral-test',
    title: 'The Integral Test and p-Series',
    summary: 'Comparing a sum with the area under a curve, and settling the family of series every later test compares against.',
    strand: 'calculus',
    levels: ['university'],
    prerequisites: ['series-convergence', 'improper-integrals'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'comparison-tests',
    title: 'Comparison and Limit Comparison',
    summary: 'Deciding a series by holding it beside one you already know, and the second version for when the inequality goes the wrong way.',
    strand: 'calculus',
    levels: ['university'],
    prerequisites: ['integral-test'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'ratio-test',
    title: 'The Ratio and Root Tests',
    summary: 'Asking how each term compares with the one before it — the test that ignores every other series in the world.',
    strand: 'calculus',
    levels: ['university'],
    prerequisites: ['comparison-tests'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'alternating-series',
    title: 'Alternating Series',
    summary: 'Terms that change sign, the series that converge only because they do, and the error bound you get for free.',
    strand: 'calculus',
    levels: ['university'],
    prerequisites: ['ratio-test'],
    status: 'available',
    estimatedMinutes: 45,
  },

  /* ------------------------------------------------------------ power series */
  {
    id: 'power-series',
    title: 'Power Series and the Radius of Convergence',
    summary: 'A polynomial with no last term, and the interval of x it is allowed to make a claim about.',
    strand: 'calculus',
    levels: ['university'],
    prerequisites: ['ratio-test', 'alternating-series'],
    status: 'available',
    estimatedMinutes: 50,
  },
  {
    id: 'taylor-series',
    title: 'Taylor and Maclaurin Series',
    summary: 'Building the power series of a function out of its derivatives at one point, and how good the first few terms are.',
    strand: 'calculus',
    levels: ['university'],
    prerequisites: ['power-series', 'derivative-rules'],
    status: 'available',
    estimatedMinutes: 50,
  },

  /* ---------------------------------------------------- parametric and polar */
  {
    id: 'parametric-curves',
    title: 'Parametric Curves',
    summary: 'Letting x and y both depend on a third variable, so a curve can loop, cross itself and go backwards.',
    strand: 'calculus',
    levels: ['university'],
    prerequisites: ['arc-length', 'derivative-rules'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'polar-calculus',
    title: 'Area and Slope in Polar Coordinates',
    summary: 'Calculus on a curve described by a radius and an angle, where the area element is a wedge rather than a strip.',
    strand: 'calculus',
    levels: ['university'],
    prerequisites: ['parametric-curves', 'polar-coordinates'],
    status: 'available',
    estimatedMinutes: 45,
  },
];
